import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { 
  getParsedCreditReportById, 
  updateParsedCreditReport,
  formatBureauName,
  getBureauTheme,
  formatTimestamp,
  getEffectiveCreditSummary
} from '../../services/creditReportService';
import './ParseCreditReport.css';

const ReportViewer = ({ reportId, onClose }) => {
  const t = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState('summary');
  const [editedData, setEditedData] = useState(null);

  useEffect(() => {
    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getParsedCreditReportById(reportId);
      setReport(result.report);
      setEditedData(JSON.parse(JSON.stringify(result.report.reportData)));
    } catch (err) {
      console.error('Error loading report:', err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateParsedCreditReport(reportId, { reportData: editedData });
      setReport(prev => ({
        ...prev,
        reportData: editedData,
        lastModifiedAt: { seconds: Date.now() / 1000 }
      }));
      setHasChanges(false);
      setEditMode(false);
    } catch (err) {
      console.error('Error saving report:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedData(JSON.parse(JSON.stringify(report.reportData)));
    setHasChanges(false);
    setEditMode(false);
  };

  const updateField = (path, value) => {
    setEditedData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newData;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="report-viewer">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('creditReportParser.loadingReport', 'Loading report...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-viewer">
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <h3>{t('creditReportParser.errorTitle', 'Error Loading Report')}</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={loadReport}>
            {t('creditReportParser.retry', 'Retry')}
          </button>
          <button className="back-btn" onClick={onClose}>
            {t('creditReportParser.goBack', 'Go Back')}
          </button>
        </div>
      </div>
    );
  }

  const theme = getBureauTheme(report?.bureau);
  const data = editMode ? editedData : report?.reportData;

  const sections = [
    { id: 'summary', label: t('creditReportParser.sections.summary', 'Summary'), icon: '📊' },
    { id: 'personal', label: t('creditReportParser.sections.personal', 'Personal Info'), icon: '👤' },
    { id: 'accounts', label: t('creditReportParser.sections.accounts', 'Accounts'), icon: '💳' },
    { id: 'inquiries', label: t('creditReportParser.sections.inquiries', 'Inquiries'), icon: '🔍' },
    { id: 'collections', label: t('creditReportParser.sections.collections', 'Collections'), icon: '⚠️' },
    { id: 'publicRecords', label: t('creditReportParser.sections.publicRecords', 'Public Records'), icon: '📜' },
  ];

  return (
    <div className="report-viewer">
      <header className="viewer-header" style={{ background: theme.gradient }}>
        <div className="header-left">
          <button className="back-button" onClick={onClose}>
            ← {t('creditReportParser.back', 'Back')}
          </button>
          <div className="report-title">
            <h1>{formatBureauName(report?.bureau)} {t('creditReportParser.report', 'Report')}</h1>
            <span className="report-date">
              {t('creditReportParser.parsedOn', 'Parsed')}: {formatTimestamp(report?.parsedAt)}
            </span>
          </div>
        </div>
        <div className="header-actions">
          {editMode ? (
            <>
              <button className="cancel-btn" onClick={handleCancel} disabled={saving}>
                {t('creditReportParser.cancel', 'Cancel')}
              </button>
              <button 
                className="save-btn" 
                onClick={handleSave} 
                disabled={!hasChanges || saving}
              >
                {saving ? t('creditReportParser.saving', 'Saving...') : t('creditReportParser.save', 'Save Changes')}
              </button>
            </>
          ) : (
            <button className="edit-btn" onClick={() => setEditMode(true)}>
              ✏️ {t('creditReportParser.edit', 'Edit')}
            </button>
          )}
        </div>
      </header>

      <div className="viewer-body">
        <nav className="section-nav">
          {sections.map(section => (
            <button
              key={section.id}
              className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className="section-icon">{section.icon}</span>
              <span className="section-label">{section.label}</span>
            </button>
          ))}
        </nav>

        <main className="section-content">
          {activeSection === 'summary' && (
            <SummarySection data={data} t={t} />
          )}
          {activeSection === 'personal' && (
            <PersonalInfoSection 
              data={data?.personalInfo} 
              editMode={editMode}
              onUpdate={(path, value) => updateField(`personalInfo.${path}`, value)}
              t={t} 
            />
          )}
          {activeSection === 'accounts' && (
            <AccountsSection 
              accounts={data?.accounts || []} 
              editMode={editMode}
              onUpdate={(index, path, value) => {
                const newAccounts = [...(editedData?.accounts || [])];
                const keys = path.split('.');
                let obj = newAccounts[index];
                for (let i = 0; i < keys.length - 1; i++) {
                  if (!obj[keys[i]]) obj[keys[i]] = {};
                  obj = obj[keys[i]];
                }
                obj[keys[keys.length - 1]] = value;
                updateField('accounts', newAccounts);
              }}
              t={t} 
            />
          )}
          {activeSection === 'inquiries' && (
            <InquiriesSection inquiries={data?.inquiries} t={t} />
          )}
          {activeSection === 'collections' && (
            <CollectionsSection collections={data?.collections || []} t={t} />
          )}
          {activeSection === 'publicRecords' && (
            <PublicRecordsSection records={data?.publicRecords || []} t={t} />
          )}
        </main>
      </div>
    </div>
  );
};

// Summary Section
const SummarySection = ({ data, t }) => {
  // Use effective summary which calculates from accounts if backend values are missing
  const summary = getEffectiveCreditSummary(data);
  
  return (
    <div className="section-panel summary-section">
      <h2>{t('creditReportParser.creditSummary', 'Credit Summary')}</h2>
      
      {/* Account Counts */}
      <div className="summary-grid">
        <div className="summary-card">
          <span className="summary-value">{summary.totalAccounts || 0}</span>
          <span className="summary-label">{t('creditReportParser.totalAccounts', 'Total Accounts')}</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{summary.openAccounts || 0}</span>
          <span className="summary-label">{t('creditReportParser.openAccounts', 'Open Accounts')}</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{summary.closedAccounts || 0}</span>
          <span className="summary-label">{t('creditReportParser.closedAccounts', 'Closed Accounts')}</span>
        </div>
        <div className="summary-card warning">
          <span className="summary-value">{summary.delinquentAccounts || 0}</span>
          <span className="summary-label">{t('creditReportParser.delinquentAccounts', 'Delinquent')}</span>
        </div>
        <div className="summary-card warning">
          <span className="summary-value">{summary.derogatoryAccounts || 0}</span>
          <span className="summary-label">{t('creditReportParser.derogatoryAccounts', 'Derogatory')}</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">
            {summary.creditUtilization != null ? `${summary.creditUtilization}%` : '--'}
          </span>
          <span className="summary-label">{t('creditReportParser.utilization', 'Utilization')}</span>
        </div>
      </div>

      {/* Balances by Category */}
      <h3 className="subsection-title">{t('creditReportParser.balancesByType', 'Balances by Type')}</h3>
      <div className="summary-grid balances-grid">
        <div className="summary-card balance-card revolving">
          <span className="balance-icon">💳</span>
          <div className="balance-details">
            <span className="summary-value">
              {summary.revolvingBalance != null ? `$${summary.revolvingBalance.toLocaleString()}` : '$0'}
            </span>
            <span className="summary-label">{t('creditReportParser.revolvingBalance', 'Credit Cards')}</span>
          </div>
        </div>
        <div className="summary-card balance-card mortgage">
          <span className="balance-icon">🏠</span>
          <div className="balance-details">
            <span className="summary-value">
              {summary.mortgageBalance != null ? `$${summary.mortgageBalance.toLocaleString()}` : '$0'}
            </span>
            <span className="summary-label">{t('creditReportParser.mortgageBalance', 'Mortgage')}</span>
          </div>
        </div>
        <div className="summary-card balance-card installment">
          <span className="balance-icon">🚗</span>
          <div className="balance-details">
            <span className="summary-value">
              {summary.installmentBalance != null ? `$${summary.installmentBalance.toLocaleString()}` : '$0'}
            </span>
            <span className="summary-label">{t('creditReportParser.installmentBalance', 'Installment Loans')}</span>
          </div>
        </div>
        <div className="summary-card balance-card other">
          <span className="balance-icon">📋</span>
          <div className="balance-details">
            <span className="summary-value">
              {summary.otherBalance != null ? `$${summary.otherBalance.toLocaleString()}` : '$0'}
            </span>
            <span className="summary-label">{t('creditReportParser.otherBalance', 'Other')}</span>
          </div>
        </div>
      </div>

      {/* Total & Monthly */}
      <div className="summary-grid totals-grid">
        <div className="summary-card highlight total-card">
          <span className="summary-value">
            {summary.totalBalances != null ? `$${summary.totalBalances.toLocaleString()}` : '--'}
          </span>
          <span className="summary-label">{t('creditReportParser.totalBalances', 'Total Debt')}</span>
        </div>
        <div className="summary-card total-card">
          <span className="summary-value">
            {summary.totalMonthlyPayments != null ? `$${summary.totalMonthlyPayments.toLocaleString()}` : '--'}
          </span>
          <span className="summary-label">{t('creditReportParser.monthlyPayments', 'Monthly Payments')}</span>
        </div>
        {summary.totalCreditLimit != null && summary.totalCreditLimit > 0 && (
          <div className="summary-card total-card">
            <span className="summary-value">
              ${summary.totalCreditLimit.toLocaleString()}
            </span>
            <span className="summary-label">{t('creditReportParser.totalCreditLimit', 'Total Credit Limit')}</span>
          </div>
        )}
      </div>

      {summary.oldestAccountDate && (
        <div className="oldest-account">
          <span className="label">{t('creditReportParser.oldestAccount', 'Oldest Account')}:</span>
          <span className="value">{summary.oldestAccountDate}</span>
        </div>
      )}
    </div>
  );
};

// Personal Info Section
const PersonalInfoSection = ({ data, editMode, onUpdate, t }) => {
  if (!data) {
    return (
      <div className="section-panel">
        <h2>{t('creditReportParser.personalInfo', 'Personal Information')}</h2>
        <p className="no-data">{t('creditReportParser.noPersonalInfo', 'No personal information available')}</p>
      </div>
    );
  }

  return (
    <div className="section-panel personal-section">
      <h2>{t('creditReportParser.personalInfo', 'Personal Information')}</h2>
      
      <div className="info-group">
        <h3>{t('creditReportParser.name', 'Name')}</h3>
        <div className="info-row">
          <EditableField
            label={t('creditReportParser.firstName', 'First Name')}
            value={data.name?.first || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('name.first', val)}
          />
          <EditableField
            label={t('creditReportParser.middleName', 'Middle')}
            value={data.name?.middle || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('name.middle', val)}
          />
          <EditableField
            label={t('creditReportParser.lastName', 'Last Name')}
            value={data.name?.last || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('name.last', val)}
          />
        </div>
      </div>

      <div className="info-group">
        <h3>{t('creditReportParser.currentAddress', 'Current Address')}</h3>
        <div className="info-row">
          <EditableField
            label={t('creditReportParser.street', 'Street')}
            value={data.currentAddress?.street || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('currentAddress.street', val)}
            wide
          />
        </div>
        <div className="info-row">
          <EditableField
            label={t('creditReportParser.city', 'City')}
            value={data.currentAddress?.city || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('currentAddress.city', val)}
          />
          <EditableField
            label={t('creditReportParser.state', 'State')}
            value={data.currentAddress?.state || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('currentAddress.state', val)}
          />
          <EditableField
            label={t('creditReportParser.zip', 'ZIP')}
            value={data.currentAddress?.zip || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('currentAddress.zip', val)}
          />
        </div>
      </div>

      <div className="info-group">
        <h3>{t('creditReportParser.otherInfo', 'Other Information')}</h3>
        <div className="info-row">
          <EditableField
            label={t('creditReportParser.ssn4', 'SSN (Last 4)')}
            value={data.ssn_last4 || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('ssn_last4', val)}
          />
          <EditableField
            label={t('creditReportParser.dob', 'Date of Birth')}
            value={data.dateOfBirth || ''}
            editMode={editMode}
            onUpdate={(val) => onUpdate('dateOfBirth', val)}
          />
        </div>
      </div>

      {data.previousAddresses && data.previousAddresses.length > 0 && (
        <div className="info-group">
          <h3>{t('creditReportParser.previousAddresses', 'Previous Addresses')}</h3>
          {data.previousAddresses.map((addr, idx) => (
            <div key={idx} className="previous-address">
              <p>{addr.street}, {addr.city}, {addr.state} {addr.zip}</p>
              {addr.reportedDate && <span className="reported-date">{addr.reportedDate}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Editable Field Component
const EditableField = ({ label, value, editMode, onUpdate, wide = false }) => {
  return (
    <div className={`editable-field ${wide ? 'wide' : ''}`}>
      <label>{label}</label>
      {editMode ? (
        <input
          type="text"
          value={value}
          onChange={(e) => onUpdate(e.target.value)}
          placeholder={label}
        />
      ) : (
        <span className="field-value">{value || '--'}</span>
      )}
    </div>
  );
};

// Accounts Section
const AccountsSection = ({ accounts, editMode, onUpdate, t }) => {
  const [expandedAccount, setExpandedAccount] = useState(null);

  if (!accounts || accounts.length === 0) {
    return (
      <div className="section-panel">
        <h2>{t('creditReportParser.accounts', 'Accounts')}</h2>
        <p className="no-data">{t('creditReportParser.noAccounts', 'No accounts found')}</p>
      </div>
    );
  }

  return (
    <div className="section-panel accounts-section">
      <h2>{t('creditReportParser.accounts', 'Accounts')} ({accounts.length})</h2>
      
      <div className="accounts-list">
        {accounts.map((account, idx) => (
          <div 
            key={idx} 
            className={`account-card ${expandedAccount === idx ? 'expanded' : ''} ${account.accountStatus?.toLowerCase().includes('closed') ? 'closed' : ''}`}
          >
            <div 
              className="account-header"
              onClick={() => setExpandedAccount(expandedAccount === idx ? null : idx)}
            >
              <div className="account-main">
                <span className="creditor-name">{account.creditorName || t('creditReportParser.unknownCreditor', 'Unknown Creditor')}</span>
                <span className="account-type">{account.accountType || t('creditReportParser.unknownType', 'Unknown Type')}</span>
              </div>
              <div className="account-status">
                <span className={`status-badge ${account.accountStatus?.toLowerCase().replace(/\s+/g, '-')}`}>
                  {account.accountStatus || t('creditReportParser.unknown', 'Unknown')}
                </span>
                <span className="balance">
                  ${account.currentBalance?.toLocaleString() || 0}
                </span>
              </div>
              <span className="expand-icon">{expandedAccount === idx ? '▼' : '▶'}</span>
            </div>

            {expandedAccount === idx && (
              <div className="account-details">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('creditReportParser.accountNumber', 'Account #')}</label>
                    <span>****{account.accountNumber_last4 || '----'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('creditReportParser.dateOpened', 'Opened')}</label>
                    <span>{account.dateOpened || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('creditReportParser.creditLimit', 'Credit Limit')}</label>
                    <span>${account.creditLimit?.toLocaleString() || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('creditReportParser.highBalance', 'High Balance')}</label>
                    <span>${account.highBalance?.toLocaleString() || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('creditReportParser.monthlyPayment', 'Monthly Payment')}</label>
                    <span>${account.monthlyPayment?.toLocaleString() || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('creditReportParser.paymentStatus', 'Payment Status')}</label>
                    <span>{account.paymentStatus || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('creditReportParser.responsibility', 'Responsibility')}</label>
                    <span>{account.responsibility || '--'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('creditReportParser.lastReported', 'Last Reported')}</label>
                    <span>{account.dateReported || '--'}</span>
                  </div>
                </div>

                {account.remarks && account.remarks.length > 0 && (
                  <div className="remarks">
                    <label>{t('creditReportParser.remarks', 'Remarks')}</label>
                    <ul>
                      {account.remarks.map((remark, i) => (
                        <li key={i}>{remark}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {account.paymentHistory && Object.keys(account.paymentHistory).length > 0 && (
                  <div className="payment-history">
                    <label>{t('creditReportParser.paymentHistory', 'Payment History')}</label>
                    <div className="history-grid">
                      {Object.entries(account.paymentHistory)
                        .sort((a, b) => b[0].localeCompare(a[0]))
                        .slice(0, 24)
                        .map(([month, status]) => (
                          <div 
                            key={month} 
                            className={`history-item ${status === 'OK' ? 'good' : 'bad'}`}
                            title={`${month}: ${status}`}
                          >
                            <span className="history-month">{month.slice(5)}</span>
                            <span className="history-status">{status}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Inquiries Section
const InquiriesSection = ({ inquiries, t }) => {
  const hardInquiries = inquiries?.hard || [];
  const softInquiries = inquiries?.soft || [];

  return (
    <div className="section-panel inquiries-section">
      <h2>{t('creditReportParser.inquiries', 'Credit Inquiries')}</h2>

      <div className="inquiry-type">
        <h3>
          <span className="type-icon">🔴</span>
          {t('creditReportParser.hardInquiries', 'Hard Inquiries')} ({hardInquiries.length})
        </h3>
        {hardInquiries.length === 0 ? (
          <p className="no-data">{t('creditReportParser.noHardInquiries', 'No hard inquiries found')}</p>
        ) : (
          <div className="inquiry-list">
            {hardInquiries.map((inq, idx) => (
              <div key={idx} className="inquiry-item hard">
                <span className="inquirer">{inq.inquirerName || t('creditReportParser.unknown', 'Unknown')}</span>
                <span className="inquiry-date">{inq.inquiryDate || '--'}</span>
                <span className="business-type">{inq.businessType || '--'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="inquiry-type">
        <h3>
          <span className="type-icon">🟢</span>
          {t('creditReportParser.softInquiries', 'Soft Inquiries')} ({softInquiries.length})
        </h3>
        {softInquiries.length === 0 ? (
          <p className="no-data">{t('creditReportParser.noSoftInquiries', 'No soft inquiries found')}</p>
        ) : (
          <div className="inquiry-list">
            {softInquiries.map((inq, idx) => (
              <div key={idx} className="inquiry-item soft">
                <span className="inquirer">{inq.inquirerName || t('creditReportParser.unknown', 'Unknown')}</span>
                <span className="inquiry-date">{inq.inquiryDate || '--'}</span>
                <span className="business-type">{inq.businessType || '--'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Collections Section
const CollectionsSection = ({ collections, t }) => {
  if (!collections || collections.length === 0) {
    return (
      <div className="section-panel">
        <h2>{t('creditReportParser.collections', 'Collections')}</h2>
        <div className="no-collections">
          <span className="success-icon">✓</span>
          <p>{t('creditReportParser.noCollections', 'No collection accounts found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-panel collections-section">
      <h2>{t('creditReportParser.collections', 'Collections')} ({collections.length})</h2>
      
      <div className="collections-list">
        {collections.map((col, idx) => (
          <div key={idx} className="collection-item">
            <div className="collection-header">
              <span className="agency">{col.collectionAgency || t('creditReportParser.unknownAgency', 'Unknown Agency')}</span>
              <span className="amount">${col.currentBalance?.toLocaleString() || col.originalAmount?.toLocaleString() || 0}</span>
            </div>
            <div className="collection-details">
              <div className="detail">
                <label>{t('creditReportParser.originalCreditor', 'Original Creditor')}</label>
                <span>{col.originalCreditor || '--'}</span>
              </div>
              <div className="detail">
                <label>{t('creditReportParser.dateOpened', 'Date Opened')}</label>
                <span>{col.dateOpened || '--'}</span>
              </div>
              <div className="detail">
                <label>{t('creditReportParser.status', 'Status')}</label>
                <span className={`status ${col.status?.toLowerCase()}`}>{col.status || '--'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Public Records Section
const PublicRecordsSection = ({ records, t }) => {
  if (!records || records.length === 0) {
    return (
      <div className="section-panel">
        <h2>{t('creditReportParser.publicRecords', 'Public Records')}</h2>
        <div className="no-records">
          <span className="success-icon">✓</span>
          <p>{t('creditReportParser.noPublicRecords', 'No public records found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-panel public-records-section">
      <h2>{t('creditReportParser.publicRecords', 'Public Records')} ({records.length})</h2>
      
      <div className="records-list">
        {records.map((record, idx) => (
          <div key={idx} className="record-item">
            <div className="record-type">
              <span className="type-badge">{record.type || t('creditReportParser.unknown', 'Unknown')}</span>
              <span className="filed-date">{t('creditReportParser.filed', 'Filed')}: {record.filedDate || '--'}</span>
            </div>
            <div className="record-details">
              <div className="detail">
                <label>{t('creditReportParser.court', 'Court')}</label>
                <span>{record.court || '--'}</span>
              </div>
              <div className="detail">
                <label>{t('creditReportParser.caseNumber', 'Case #')}</label>
                <span>{record.caseNumber || '--'}</span>
              </div>
              <div className="detail">
                <label>{t('creditReportParser.status', 'Status')}</label>
                <span>{record.status || '--'}</span>
              </div>
              {record.amount && (
                <div className="detail">
                  <label>{t('creditReportParser.amount', 'Amount')}</label>
                  <span>${record.amount.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportViewer;

